// ─── LexAuto Tickets — core domain types ─────────────────────────────────────
// User-friendly naming: Ticket = any work item (task / bug / story).

export type TicketType = 'Story' | 'Task' | 'Bug' | 'Epic' | 'Subtask';
// Status is a free string — every project defines its own workflow columns
// (e.g. Todo → In Progress → INQA → Done). The `category` on CustomStatus
// tells the app what each column *means* (done vs not done).
export type TicketStatus = string;
export type TicketPriority = 'Highest' | 'High' | 'Medium' | 'Low' | 'Lowest';
export type SprintState = 'backlog' | 'active' | 'done';

// Extensible categories — built-ins are To do / In progress / Done; projects may add more.
export type StatusCategory = string;

export interface CustomStatus {
  id: string;
  name: string;
  color: string;
  category: StatusCategory;
}

export const DEFAULT_STATUSES: CustomStatus[] = [
  { id: 'todo', name: 'Todo', color: '#6b7280', category: 'todo' },
  { id: 'inprogress', name: 'In Progress', color: '#2563eb', category: 'inprogress' },
  { id: 'inreview', name: 'In Review', color: '#d97706', category: 'inprogress' },
  { id: 'done', name: 'Done', color: '#16a34a', category: 'done' }
];

export const STATUS_COLOR_PALETTE = ['#6b7280', '#2563eb', '#d97706', '#16a34a', '#7c3aed', '#0891b2', '#db2777', '#ea580c'];

// Default labels for the built-in categories — projects can add their own.
export const CATEGORY_LABEL: Record<string, string> = {
  todo: 'To do',
  inprogress: 'In progress',
  done: 'Done'
};

export interface TicketAttachment {
  id: string;
  uid?: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  dataUrl?: string; // base64 encoded file or fallback
  fileUrl?: string; // cloud URL or download endpoint
  storageKey?: string;
  storageProvider?: string;
  uploadedBy: string;
  uploadedAt: string;
}

export interface TicketComment {
  id: string;
  author: string;
  text: string;
  createdAt: string;
}

export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

// Jira-style issue links (any ticket ↔ any ticket, directed):
// 'relates' = relates to · 'blocks' = this blocks target · 'blocked' = this is blocked by target
export type LinkRelation = 'relates' | 'blocks' | 'blocked';

export interface TicketLink {
  id: string;
  targetId: string;
  relation: LinkRelation;
}

export const LINK_RELATIONS: { value: LinkRelation; label: string }[] = [
  { value: 'relates', label: 'relates to' },
  { value: 'blocks', label: 'blocks' },
  { value: 'blocked', label: 'is blocked by' }
];

export const inverseRelation = (r: LinkRelation): LinkRelation =>
  r === 'blocks' ? 'blocked' : r === 'blocked' ? 'blocks' : 'relates';

export interface ResolutionOption {
  value: string;
  label: string;
  description: string;
  category: 'success' | 'warning' | 'neutral' | 'error';
}

export const JIRA_RESOLUTIONS: ResolutionOption[] = [
  { value: 'Fixed', label: 'Fixed', description: 'A fix for that issue is verified and complete', category: 'success' },
  { value: 'Done', label: 'Done', description: 'Work has been completed as described', category: 'success' },
  { value: "Won't Fix", label: "Won't Fix", description: 'The problem will not be addressed (obsolete/out of scope)', category: 'warning' },
  { value: "Won't Do", label: "Won't Do", description: 'The team has decided not to do this work', category: 'warning' },
  { value: 'Duplicate', label: 'Duplicate', description: 'Duplicates another existing issue or defect', category: 'neutral' },
  { value: 'Cannot Reproduce', label: 'Cannot Reproduce', description: 'All attempts to reproduce this issue failed', category: 'neutral' },
  { value: 'Incomplete', label: 'Incomplete', description: 'Not enough information provided to reproduce or complete', category: 'neutral' },
  { value: 'Invalid', label: 'Invalid', description: 'The issue is not valid or was created by mistake', category: 'error' },
  { value: 'Declined', label: 'Declined', description: 'The request has been reviewed and declined', category: 'error' },
  { value: 'Rejected', label: 'Rejected', description: 'Rejected during code review or QA verification', category: 'error' }
];

export interface Ticket {
  id: string;
  key: string; // e.g. WEB-12
  projectId: string;
  title: string;
  description: string;
  type: TicketType;
  status: TicketStatus;
  resolution?: string; // e.g. Fixed, Invalid, Won't Fix, Duplicate, etc.
  resolvedAt?: string;
  priority: TicketPriority;
  points: number;
  assignee: string;
  reporter: string;
  dueDate?: string; // ISO date — shown on card after the title
  labels: string[];
  // Strict Jira-style hierarchy:
  // Epic (no parent) → Story/Task/Bug (parent must be an Epic)
  // Story/Task/Bug → Subtask (parent must be a standard issue, never an Epic)
  parentId: string | null;
  links: TicketLink[]; // Jira-style issue links (any ↔ any, mirrored on both sides)
  components: string[]; // managed project component UIDs (multi-select, Jira-style)
  moduleUid?: string; // Lexauto module of the linked real project (filterable)
  testCaseUid?: string; // Lexauto testcase linked to this ticket (failure sync + live report panel)
  // Status-change log — powers the burndown chart & audit history timeline. Appended on create + every status move.
  statusHistory: { status: TicketStatus; at: string; by?: string; resolution?: string }[];
  sprintId: string | null; // null = Backlog
  createdAt: string;
  updatedAt: string;
  comments: TicketComment[];
  checklist: ChecklistItem[];
  attachments: TicketAttachment[];
}

export interface TicketSprint {
  id: string;
  projectId: string;
  name: string;
  goal: string;
  state: SprintState;
  startDate?: string;
  endDate?: string;
  createdAt: string;
}

export interface TicketProject {
  id: string;
  key: string; // e.g. WEB
  name: string;
  description: string;
  color: string;
  counter: number; // for ticket key numbering
  statuses: CustomStatus[]; // this project's workflow columns, in board order
  members: string[]; // team roster — assignee suggestions + avatar filter row
  assigneeRequired?: boolean; // false = assignee optional, blank defaults to first team member (project lead). Default true.
  linkedProjectUid?: string; // Lexauto project uid — workspace auto-provisioned from real projects
  createdAt: string;
}

export interface TicketBoardState {
  projects: TicketProject[];
  sprints: TicketSprint[];
  tickets: Ticket[];
}

export const TICKET_TYPES: TicketType[] = ['Story', 'Task', 'Bug', 'Epic', 'Subtask'];
export const TICKET_PRIORITIES: TicketPriority[] = ['Highest', 'High', 'Medium', 'Low', 'Lowest'];
